import { useEffect, useState } from "react";
import { ApiError, api, type GetWitnessLogEntryResponse } from "../api/client";
import { receiptDetailPath } from "../routes";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: GetWitnessLogEntryResponse }
  | { kind: "missing" }
  | { kind: "error"; message: string };

export function WitnessLogDetailPage(props: { position: number; onBack: () => void }) {
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    (async () => {
      try {
        const result = await api.getWitnessLogEntry(props.position);
        if (cancelled) return;
        setState({ kind: "ready", data: result });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: "missing" });
          return;
        }
        const message = err instanceof Error ? err.message : "Could not load entry.";
        setState({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.position]);

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <button className="btn btn-secondary" onClick={props.onBack}>
          ← Back to witness log
        </button>
      </div>
      {renderBody(state)}
    </>
  );
}

function renderBody(state: LoadState) {
  if (state.kind === "loading") {
    return (
      <div className="panel">
        <p className="muted">Loading entry…</p>
      </div>
    );
  }
  if (state.kind === "missing") {
    return (
      <div className="panel">
        <h2 className="section-title">Entry not found</h2>
        <p>
          No entry at this position belongs to your account. Witness log entries are scoped per
          customer; a position owned by another customer reads as missing here.
        </p>
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="panel">
        <h2 className="section-title">Could not load entry</h2>
        <div className="error">{state.message}</div>
      </div>
    );
  }
  const { entry, chain_status: chainStatus, batch_status: batchStatus } = state.data;
  return (
    <>
      <div className="panel">
        <div className="section-header">
          <h2 className="section-title">Witness log entry #{entry.position}</h2>
          <span className="section-meta" title={entry.created_at}>
            {formatTimestamp(entry.created_at)}
          </span>
        </div>
        <dl className="kv-grid">
          <dt>Log id</dt>
          <dd>
            <code className="muted-code">{entry.log_id}</code>
          </dd>
          <dt>Entry hash</dt>
          <dd>
            <FieldCopyable value={entry.entry_hash} />
          </dd>
          <dt>Previous entry hash</dt>
          <dd>
            {entry.previous_entry_hash ? (
              <FieldCopyable value={entry.previous_entry_hash} />
            ) : (
              <span className="muted">(none)</span>
            )}
          </dd>
          <dt>Chain id</dt>
          <dd>
            {entry.chain_id ? (
              <code className="muted-code">{entry.chain_id}</code>
            ) : (
              <span className="muted">(none)</span>
            )}
          </dd>
          <dt>Chain state</dt>
          <dd>
            {entry.chain_state ? (
              <FieldCopyable value={entry.chain_state} />
            ) : (
              <span className="muted">(none)</span>
            )}
          </dd>
          <dt>Witness signature</dt>
          <dd>
            <FieldCopyable value={entry.witness_signature} />
          </dd>
          <dt>Witness key id</dt>
          <dd>
            <code className="muted-code">{entry.key_id}</code>
          </dd>
          <dt>API key</dt>
          <dd>
            {entry.api_key_prefix ? (
              <code className="muted-code">{entry.api_key_prefix}</code>
            ) : (
              <span className="muted">(unknown)</span>
            )}
          </dd>
          {entry.receipt_schema_version ? (
            <>
              <dt>Schema version</dt>
              <dd>
                <code className="muted-code">{entry.receipt_schema_version}</code>
              </dd>
            </>
          ) : null}
        </dl>
      </div>
      <div className="panel">
        <h2 className="section-title">Chain context</h2>
        {chainStatus.evidence_redacted_at ? (
          // Per-receipt erasure (db/migrations/0023): the receipt and its
          // hosted evidence were deleted from the platform. Distinct from
          // "direct mode" — an erased managed receipt also has no stored
          // receipt, but here it WAS stored and was erased, so the custody
          // statement is "may hold if exported", not "holds". Checked before
          // the stored/direct branches since an erased entry has no live
          // receipt and would otherwise fall through to "direct mode".
          <p>
            <span className="badge badge-erased" style={{ marginRight: 8 }}>
              deleted
            </span>
            This chain&rsquo;s receipt and hosted evidence were erased from Sequesign. If the
            package was exported before erasure, the customer may still hold the envelope. The entry
            hash, position, and inclusion proof remain intact.
          </p>
        ) : chainStatus.has_stored_receipt && chainStatus.receipt_id ? (
          // Managed-mode: the badge mirrors the "direct mode" badge
          // below so a reviewer sees envelope custody as a single
          // dimension. Hash-only and hosted tiers both land here
          // because both store the envelope at Sequesign; the
          // evidence-custody distinction (which only matters for
          // hash-only) is surfaced on the receipt detail page, not
          // here.
          <p>
            <span className="badge badge-live" style={{ marginRight: 8 }}>
              stored
            </span>
            Receipt envelope stored at Sequesign.{" "}
            <a className="receipt-link" href={receiptDetailPath(chainStatus.receipt_id)}>
              View related receipt {chainStatus.receipt_id}
            </a>
            .
          </p>
        ) : chainStatus.chain_id === null ? (
          // chain_id nulled with no redaction marker: an account-scope deletion
          // (scrubLogEntriesForCustomer nulls chain_id) or a legacy per-receipt
          // erasure from before the evidence_redacted_at marker existed. Mirror
          // the list view's "erased" state here rather than falling through to
          // "direct mode", which would repeat the false "customer holds the
          // envelope" claim for an entry that was actually erased.
          <p>
            <span className="badge badge-erased" style={{ marginRight: 8 }}>
              erased
            </span>
            This entry&rsquo;s chain and receipt linkage were erased under data deletion. The entry
            hash, position, and inclusion proof remain intact.
          </p>
        ) : (
          <p>
            <span className="badge badge-test" style={{ marginRight: 8 }}>
              direct mode
            </span>
            No stored receipt at Sequesign. The customer holds the envelope.
          </p>
        )}
        {chainStatus.chain_id ? (
          <p className="muted">
            Total entries in this chain (this customer):{" "}
            <strong>{chainStatus.total_entries_in_chain}</strong>
          </p>
        ) : null}
      </div>
      <div className="panel">
        <h2 className="section-title">Batch status</h2>
        {batchStatus.batched ? (
          <dl className="kv-grid">
            <dt>Batch id</dt>
            <dd>
              <code className="muted-code">#{batchStatus.batch_id}</code>
            </dd>
            <dt>Sealed at</dt>
            <dd title={batchStatus.batch_sealed_at}>
              {formatTimestamp(batchStatus.batch_sealed_at)}
            </dd>
            <dt>Tree size</dt>
            <dd>{batchStatus.tree_size}</dd>
            <dt>Merkle root</dt>
            <dd>
              <FieldCopyable value={batchStatus.merkle_root} />
            </dd>
            <dt>Witness signature on batch</dt>
            <dd>
              <FieldCopyable value={batchStatus.witness_signature} />
            </dd>
          </dl>
        ) : batchStatus.estimated_seal_in_seconds === null ? (
          <p>Not yet batched. The next seal is imminent.</p>
        ) : (
          <p>
            Not yet batched. Expected within{" "}
            <strong>{formatDuration(batchStatus.estimated_seal_in_seconds)}</strong>. Reload to
            refresh.
          </p>
        )}
      </div>
    </>
  );
}

function FieldCopyable(props: { value: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    void navigator.clipboard
      .writeText(props.value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Clipboard write may fail (insecure context, denied permission).
        // The value is still visible inline; we silently no-op.
      });
  }
  return (
    <span className="copyable-row">
      <code className="muted-code">{props.value}</code>
      <button
        type="button"
        className="btn btn-secondary btn-small"
        onClick={copy}
        aria-label="Copy to clipboard"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </span>
  );
}

function formatTimestamp(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const date = new Date(parsed);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))} sec`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  return `${(seconds / 3600).toFixed(1)} hr`;
}
